// src/config/devices.js

// ── Campus mapping ──────────────────────────────────────────────────────────
export const CAMPUS_MAP = {
  "kingston campus": "CCK",
  "wyndham campus":  "CCW",
  "hcc": "CCK",
  "wcc": "CCW",
};

export function extractCampus(ownerDetails) {
  if (!ownerDetails) return "Unknown";
  const first = ownerDetails.split(/\n/)[0].replace("Device Owner Name:", "").trim();
  const parts = first.split("-");
  const raw = (parts.length >= 2 ? parts[parts.length - 1] : first).trim().toLowerCase();
  return CAMPUS_MAP[raw] || (parts.length >= 2 ? parts[parts.length - 1].trim() : first);
}

export function campusFromOrderNumber(orderNo) {
  const value = String(orderNo || "").toLowerCase();
  if (value.includes("wcc")) return "CCW";
  if (value.includes("hcc")) return "CCK";
  return "Unknown";
}

// ── Hardcoded X13 staff serials (confirmed via Jira Assets export) ──────────
// These are the only X13s treated as staff devices — no new X13s are purchased for staff
export const X13_STAFF_SERIALS = new Set([
  "R9130ZM0","R9130ZM9","PW02TV1H",
  "PF4RWX1R","PF4RWX20","PF4RWX2A","PF4RWX2F","PF4RWX2P",
  "PF4QX749","PF4QX99W","PF4QYK24",
  "PF4WSS5C","PF4WSS4X","PF4WEYYQ","PF4WEYZT","PF4WEZ1X",
  "PF4WSQY3","PF4WSS8E","PF4WEZ1C",
  "PF53W5Q4","PF54D5GY","PF54DE2V","PF54DE3A","PF54DE3N","PF54GXMV","PF54GXQW",
  "PF5DJC7Y","PF5DZV92","PF5E2MAG","PF5DJC6V","PF5E2M77",
]);

// ── Device classification ───────────────────────────────────────────────────
const ACCESSORY_RE = /hub|hdmi|cable|adapter|monitor|display|headset|keyboard|mouse|charger|bag|case|sleeve|warranty|care|licen|ups|printer|scanner/i;
const DOCK_RE = /dock|docking|thunderbolt\s+\d?\s*dock|usb-?c\s+dock/i;

export function classifyDevice(serial, description) {
  if (!description) return null;
  if (ACCESSORY_RE.test(description)) return null;
  if (DOCK_RE.test(description)) return "Accessory";
  if (/thinkpad\s+x1(?!3)/i.test(description)) return "Staff Device";
  if (/thinkpad\s+t14s/i.test(description))    return "Staff Device";
  if (/thinkcentre/i.test(description))         return "Staff Device";
  if (/lenovo/i.test(description) && /\bdesktop\b|small form factor|\bsff\b|\btiny\b/i.test(description)) return "Staff Device";
  if (/macbook|apple\s+mac/i.test(description)) return "Staff Device";
  if (X13_STAFF_SERIALS.has(serial))            return "Staff Device";
  return null; // student / excluded
}

function vendorFromDescription(description = "") {
  if (/apple|macbook/i.test(description)) return "Apple";
  if (/microsoft|surface/i.test(description)) return "Microsoft";
  if (/dell|optiplex|latitude/i.test(description)) return "Dell";
  if (/lenovo|thinkpad|thinkcentre|thinkbook|ideapad/i.test(description)) return "Lenovo";
  return "";
}

// ── EOL calculation ─────────────────────────────────────────────────────────
export function eolYear(dateVal) {
  if (!dateVal) return null;
  const d = new Date(dateVal);
  return isNaN(d) ? null : d.getFullYear() + 4;
}

// ── Pricing ─────────────────────────────────────────────────────────────────
export const DEFAULT_PRICE = 1704;

export const PRICES = {
  "Staff Device": DEFAULT_PRICE,
};

export const fmt = (n) =>
  n?.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

// ── RFC 4180 CSV parser ─────────────────────────────────────────────────────
export function parseCSV(text) {
  const rows = [];
  let cur = "", inQ = false, row = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQ && text[i+1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) { row.push(cur); cur = ""; }
    else if ((ch === '\n' || ch === '\r') && !inQ) {
      row.push(cur); if (row.some(c => c)) rows.push(row);
      cur = ""; row = [];
      if (ch === '\r' && text[i+1] === '\n') i++;
    } else cur += ch;
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows;
}

export function parseLWTCSV(text) {
  const rows = parseCSV(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map(h => h.trim().toLowerCase());
  const col = (...terms) => {
    for (const t of terms) {
      const i = headers.findIndex(h => h.includes(t));
      if (i !== -1) return i;
    }
    return -1;
  };
  const iSerial   = col("serial");
  const iDesc     = col("product description", "description");
  const iOwner    = col("owner details", "owner");
  const iDate     = col("date ordered", "purchase date", "date");
  const iWarranty = col("warranty date", "warranty");
  const iOrderNo  = col("order no", "order number");
  const iInvoice  = col("invoice");

  return rows.slice(1).map(v => {
    const g = i => (i >= 0 && v[i] !== undefined ? v[i].trim() : "");
    const serial = g(iSerial);
    if (!serial) return null;
    const description = g(iDesc);
    const deviceType  = classifyDevice(serial, description);
    const dateOrdered = g(iDate);
    const orderNo = g(iOrderNo);
    const ownerCampus = extractCampus(g(iOwner));
    return {
      serial, description,
      deviceType: deviceType || "Other",
      isCompute: !!deviceType && deviceType !== "Accessory",
      assetCategory: deviceType === "Accessory" ? "Docking Station" : "Computer",
      vendor: vendorFromDescription(description),
      campus: ownerCampus !== "Unknown" ? ownerCampus : campusFromOrderNumber(orderNo),
      dateOrdered, warrantyDate: g(iWarranty),
      orderNo, invoiceId: g(iInvoice),
      eolYear: eolYear(dateOrdered),
      // Jira enrichment (populated later)
      assignedTo: null, assignedEmail: null, jiraKey: null, jiraStatus: null,
    };
  }).filter(Boolean);
}
